import { useContext, useMemo, useState } from 'react';
import { useGuardrailPolicies } from '@/api/rpc/hooks/guardrail-policies/useGuardrailPolicies';
import type { GuardrailPolicySummary } from '@erp/rpc';
import {
  getWorkflowGuardrailAssignmentConfigs,
  withWorkflowGuardrailAssignmentConfigs,
  type WorkflowGuardrailAssignmentConfig,
} from '@erp/workflows';
import { AuthContext } from '@/context/auth.context';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from 'src/components/ui/switch';
import {
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { sectionPath } from '@/pages/authenticated/settings/organization/organizationSettingsSections';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { T } from 'gt-react';

const KIND_LABELS: Record<string, string> = {
  openai_moderation: 'Moderation',
  regex: 'Regex',
  prompt_injection: 'Injection',
  content_safety: 'Safety',
};

interface GuardrailPolicyAssignmentsProps {
  config: Record<string, unknown>;
  onConfigChange: (newConfig: Record<string, unknown>) => void;
  readOnly?: boolean;
  /** When true, the parent panel section supplies the heading — skip the inner disclosure. */
  embedded?: boolean;
}

export function GuardrailPolicyAssignments({
  config,
  onConfigChange,
  readOnly = false,
  embedded = false,
}: GuardrailPolicyAssignmentsProps) {
  const { currentUser } = useContext(AuthContext);
  const organizationId = currentUser?.organizationId ?? '';
  const [showPicker, setShowPicker] = useState(false);

  const { data, isLoading } = useGuardrailPolicies(organizationId);
  const assignments = useMemo(
    () => getWorkflowGuardrailAssignmentConfigs(config),
    [config],
  );
  const policiesById = useMemo(
    () => new Map((data?.policies ?? []).map((p) => [p.id, p])),
    [data?.policies],
  );

  function updateAssignments(next: WorkflowGuardrailAssignmentConfig[]) {
    onConfigChange(withWorkflowGuardrailAssignmentConfigs(config, next));
  }

  function handleAdd(policy: GuardrailPolicySummary) {
    if (assignments.some((a) => a.guardrailDefinitionId === policy.id)) {
      setShowPicker(false);
      return;
    }

    const maxSort = Math.max(0, ...assignments.map((a) => a.sortOrder));
    updateAssignments([
      ...assignments,
      {
        guardrailDefinitionId: policy.id,
        sortOrder: maxSort + 1,
        enabled: true,
      },
    ]);
    setShowPicker(false);
  }

  function handleRemove(definitionId: string) {
    updateAssignments(
      assignments
        .filter((a) => a.guardrailDefinitionId !== definitionId)
        .map((a, i) => ({ ...a, sortOrder: i })),
    );
  }

  function handleToggle(definitionId: string, enabled: boolean) {
    updateAssignments(
      assignments.map((a) =>
        a.guardrailDefinitionId === definitionId ? { ...a, enabled } : a,
      ),
    );
  }

  function handleReorder(index: number, direction: 'up' | 'down') {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= assignments.length) return;

    const reordered = [...assignments];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, moved);
    updateAssignments(reordered.map((a, i) => ({ ...a, sortOrder: i })));
  }

  const body = (
    <div className="space-y-3">
      {isLoading && assignments.length === 0 && (
        <div className="flex items-center justify-center py-4">
          <Spinner className="h-4 w-4" />
          <span className="ml-2 text-xs text-muted-foreground">
            <T context="guardrail-policies">Loading policies...</T>
          </span>
        </div>
      )}

      {assignments.length === 0 && (
        <p className="text-xs text-muted-foreground py-2">
          <T context="guardrail-policies">
            No guardrail policies assigned. Add policies to check inputs and
            outputs of this step.
          </T>
        </p>
      )}

      {assignments.map((assignment, index) => (
        <AssignmentRow
          key={assignment.guardrailDefinitionId}
          assignment={assignment}
          policy={policiesById.get(assignment.guardrailDefinitionId)}
          index={index}
          total={assignments.length}
          readOnly={readOnly}
          onRemove={() => handleRemove(assignment.guardrailDefinitionId)}
          onToggle={(enabled) =>
            handleToggle(assignment.guardrailDefinitionId, enabled)
          }
          onMoveUp={() => handleReorder(index, 'up')}
          onMoveDown={() => handleReorder(index, 'down')}
        />
      ))}

      {!readOnly && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setShowPicker(true)}
        >
          <PlusIcon className="h-3.5 w-3.5 mr-1.5" />
          <T context="guardrail-policies">Add Policy</T>
        </Button>
      )}

      {showPicker && (
        <PolicyPickerDialog
          organizationId={organizationId}
          assignedIds={assignments.map((a) => a.guardrailDefinitionId)}
          onSelect={handleAdd}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );

  if (embedded) {
    return body;
  }

  return (
    <details className="mt-6 border rounded-lg" open={assignments.length > 0}>
      <summary className="cursor-pointer px-4 py-3 font-medium text-sm flex items-center gap-2">
        <ShieldCheckIcon className="h-4 w-4" />
        <T context="guardrail-policies">Guardrail Policies</T>
        {assignments.length > 0 && (
          <Badge variant="secondary" className="ml-1 text-xs">
            {assignments.length}
          </Badge>
        )}
      </summary>

      <div className="px-4 pb-4">{body}</div>
    </details>
  );
}

interface AssignmentRowProps {
  assignment: WorkflowGuardrailAssignmentConfig;
  policy?: GuardrailPolicySummary;
  index: number;
  total: number;
  readOnly?: boolean;
  onRemove: () => void;
  onToggle: (enabled: boolean) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function AssignmentRow({
  assignment,
  policy,
  index,
  total,
  readOnly,
  onRemove,
  onToggle,
  onMoveUp,
  onMoveDown,
}: AssignmentRowProps) {
  const action = policy?.latestVersion?.action;
  const policyName = policy?.name ?? 'Unknown policy';
  const policyKind = policy?.kind ?? 'unknown';

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border p-2 text-sm',
        !assignment.enabled && 'opacity-50',
      )}
    >
      {!readOnly && (
        <div className="flex flex-col gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            disabled={index === 0}
            onClick={onMoveUp}
          >
            <ArrowUpIcon className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            disabled={index === total - 1}
            onClick={onMoveDown}
          >
            <ArrowDownIcon className="h-3 w-3" />
          </Button>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <a
            href={`${sectionPath('guardrail-policies')}?policyId=${assignment.guardrailDefinitionId}`}
            className="font-medium text-xs truncate hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {policyName}
          </a>
          <Badge variant="outline" className="text-[10px] py-0 px-1">
            {KIND_LABELS[policyKind] ?? policyKind}
          </Badge>
          {action && (
            <Badge
              variant={action === 'block' ? 'destructive' : 'secondary'}
              className="text-[10px] py-0 px-1"
            >
              {action}
            </Badge>
          )}
        </div>
      </div>

      {!readOnly && (
        <>
          <Switch
            checked={assignment.enabled}
            onCheckedChange={onToggle}
            className="scale-75"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={onRemove}
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}

interface PolicyPickerDialogProps {
  organizationId: string;
  assignedIds: string[];
  onSelect: (policy: GuardrailPolicySummary) => void;
  onClose: () => void;
}

function PolicyPickerDialog({
  organizationId,
  assignedIds,
  onSelect,
  onClose,
}: PolicyPickerDialogProps) {
  const { data, isLoading } = useGuardrailPolicies(organizationId);
  const policies = (data?.policies ?? []).filter(
    (policy) => !assignedIds.includes(policy.id),
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="guardrail-policies">Add Guardrail Policy</T>
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner className="h-5 w-5" />
          </div>
        )}

        {!isLoading && policies.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            {assignedIds.length > 0 ? (
              <T context="guardrail-policies">
                All policies are already assigned to this step.
              </T>
            ) : (
              <T context="guardrail-policies">
                No guardrail policies exist. Create one in Organization
                Settings.
              </T>
            )}
          </p>
        )}

        {!isLoading && policies.length > 0 && (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {policies.map((policy) => (
              <button
                key={policy.id}
                type="button"
                className={cn(
                  'w-full text-left rounded-md border p-3 transition-colors',
                  'hover:bg-accent hover:border-primary/30',
                )}
                onClick={() => onSelect(policy)}
              >
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-primary shrink-0" />
                  <span className="font-medium text-sm">{policy.name}</span>
                  <Badge variant="outline" className="text-xs py-0">
                    {KIND_LABELS[policy.kind] ?? policy.kind}
                  </Badge>
                </div>
                {policy.description && (
                  <p className="text-xs text-muted-foreground mt-1 ml-6 truncate">
                    {policy.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
